import React from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import Tooltip from '@material-ui/core/Tooltip';
import StatusBadge from './StatusBadge';
import { formatLatency } from './utils';
import { useStyles } from './styles';
import moment from 'moment';

const DetailRow = ({ label, value }) => {
  const classes = useStyles();
  return (
    <div className={classes.detailRow}>
      <span className={classes.detailLabel}>{label}</span>
      <span className={classes.detailValue}>{value !== null && value !== undefined ? value : '–'}</span>
    </div>
  );
};

const flattenObject = (obj, prefix = '') => {
  if (!obj || typeof obj !== 'object') return {};
  return Object.entries(obj).reduce((acc, [key, val]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(acc, flattenObject(val, fullKey));
    } else {
      acc[fullKey] = Array.isArray(val) ? JSON.stringify(val) : String(val ?? '');
    }
    return acc;
  }, {});
};

const PayloadBlock = ({ title, content }) => {
  const classes = useStyles();

  const handleCopy = () => {
    const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    navigator.clipboard.writeText(text);
  };

  return (
    <div className={classes.payloadSection}>
      <div className={classes.payloadHeader}>
        <strong style={{ fontSize: 14, color: '#263238' }}>{title}</strong>
        <Tooltip title='Copy to clipboard'>
          <IconButton size='small' onClick={handleCopy}>
            <FileCopyIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </div>
      <pre className={classes.payloadBlock}>
        {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
      </pre>
    </div>
  );
};

const PayloadTable = ({ title, content }) => {
  const classes = useStyles();

  const handleCopy = () => {
    const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    navigator.clipboard.writeText(text);
  };

  const parsed = (() => {
    if (typeof content === 'string') {
      try { return JSON.parse(content); } catch { return null; }
    }
    return content;
  })();

  const rows = parsed && typeof parsed === 'object' ? flattenObject(parsed) : null;

  return (
    <div className={classes.payloadSection}>
      <div className={classes.payloadHeader}>
        <strong style={{ fontSize: 14, color: '#263238' }}>{title}</strong>
        <Tooltip title='Copy to clipboard'>
          <IconButton size='small' onClick={handleCopy}>
            <FileCopyIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </div>
      {rows ? (
        <table className={classes.payloadTable}>
          <tbody>
            {Object.entries(rows).map(([key, val]) => (
              <tr key={key}>
                <td className={classes.payloadTableKey}>{key}</td>
                <td className={classes.payloadTableValue}>{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <pre className={classes.payloadBlock}>
          {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
        </pre>
      )}
    </div>
  );
};

const DetailPanel = ({ open, detail, loading, onClose }) => {
  const classes = useStyles();

  return (
    <Drawer anchor='right' open={open} onClose={onClose}>
      <div className={classes.detailDrawer}>
        <div className={classes.detailHeader}>
          <strong style={{ fontSize: 16, color: '#263238' }}>SAP Message Detail</strong>
          <IconButton size='small' onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <CircularProgress />
          </div>
        )}

        {!loading && !detail && (
          <div style={{ color: '#f44336', padding: 16 }}>
            Failed to load detail. Please try again.
          </div>
        )}

        {!loading && detail && (
          <>
            <DetailRow label='Direction' value={detail.direction} />
            <DetailRow label='Flow Type' value={detail.flow_type} />
            <DetailRow label='Status' value={<StatusBadge status={detail.status} />} />
            <DetailRow
              label='Timestamp'
              value={
                detail.created_at
                  ? moment(detail.created_at).format('DD MMM YYYY, HH:mm:ss')
                  : '–'
              }
            />
            <DetailRow
              label='Latency'
              value={
                detail.latency_ms !== null && detail.latency_ms !== undefined
                  ? `${detail.latency_ms} ms (${formatLatency(detail.latency_ms)})`
                  : '–'
              }
            />
            <DetailRow label='Endpoint' value={detail.endpoint} />
            <DetailRow label='HTTP Method' value={detail.http_method} />
            <DetailRow label='Node' value={detail.node_id} />
            <DetailRow label='Initiated By' value={detail.initiated_by} />
            <DetailRow label='Truncated' value={detail.payload_truncated ? 'Yes' : 'No'} />

            {detail.payload_truncated && (
              <div className={classes.truncatedWarning}>
                Payload was truncated. Original size exceeded 64 KB.
              </div>
            )}

            {detail.error_message && (
              <div className={classes.errorBox}>
                <strong>Error:</strong> {detail.error_message}
              </div>
            )}

            {detail.request_payload !== undefined && (
              <PayloadTable title='Request Payload' content={detail.request_payload} />
            )}

            {detail.response_body !== undefined && (
              <PayloadBlock title='Response Body' content={detail.response_body} />
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default DetailPanel;
